"use client";
import React, { useEffect, useState } from "react";
import { getCourseScheduleInfo, type Course } from "@/components/CourseCard";

export default function RegisterModal({ course, onClose }: { course: Course | null; onClose: () => void }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState(false);

  useEffect(() => {
    setLoading(false);
    setError(null);
    setOk(false);
  }, [course?.id]);

  if (!course) return null;
  const schedule = getCourseScheduleInfo(course.startDate);
  const isFinished = schedule.status === "finished";
  const isComingSoon = Boolean(course.comingSoon);
  const isUnavailable = schedule.isUnavailable || isComingSoon;

  async function handleSubmit(formData: FormData) {
    setLoading(true);
    setError(null);
    setOk(false);
    try {
      const privacy = formData.get("privacyAccepted");
      if (!privacy) {
        setError("Debes aceptar la política de privacidad");
        setLoading(false);
        return;
      }

      const res = await fetch("/api/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          student: formData.get("name"),
          email: formData.get("email"),
          phone: formData.get("phone"),
          academicDegree: formData.get("academicDegree"),
          message: isUnavailable
            ? `[Interés en próxima edición] ${formData.get("message") || "Solicita aviso cuando vuelva a estar disponible."}`
            : formData.get("message"),
          course: course?.title,
          courseId: course?.id,
          apoloScheduleId: course?.apoloScheduleId,
          location: course?.location,
          dardeActive: formData.get("dardeActive"),
          employmentStatus: formData.get("employmentStatus"),
          privacyAccepted: true,
        }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || "Error");
      setOk(true);
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : "Error";
      setError(message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-semibold text-slate-800">
            {isUnavailable ? "Próxima edición" : "Inscripción"}: {course.title}
          </h3>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-800">✕</button>
        </div>
        {ok ? (
          <div className="py-10 text-center">
            <h4 className="text-xl font-semibold text-green-600">¡Solicitud enviada!</h4>
            <p className="mt-2 text-slate-600">
              {isUnavailable
                ? "Te avisaremos cuando haya una nueva edición disponible."
                : "Gracias por tu interés. Nos pondremos en contacto contigo pronto."}
            </p>
            <button onClick={onClose} className="mt-6 btn-primary">Cerrar</button>
          </div>
        ) : (
          <form
            action={handleSubmit}
            className="mt-6 grid grid-cols-1 gap-4"
          >
            {isUnavailable && (
              <p className="rounded-md bg-amber-50 p-4 text-sm leading-6 text-amber-900">
                {isComingSoon
                  ? "La próxima edición todavía no tiene fecha. Déjanos tus datos y te avisaremos cuando se abra la convocatoria."
                  : isFinished
                  ? "Este curso ya ha finalizado. Déjanos tus datos y te avisaremos cuando haya una nueva edición disponible."
                  : "Este curso ya está en marcha y su plazo de inscripción ha finalizado. Ahora mismo no está disponible; déjanos tus datos y te avisaremos cuando vuelva a estarlo."}
              </p>
            )}
            <input name="name" required placeholder="Nombre completo" className="input-field" />
            <input name="email" type="email" required placeholder="Email" className="input-field" />
            <input name="phone" placeholder="Teléfono" className="input-field" />
            <input name="academicDegree" placeholder="Titulación académica" className="input-field" />
            <textarea name="message" placeholder="¿Por qué te interesa este curso?" rows={4} className="input-field" />
            <fieldset className="rounded-md border border-emerald-900/10 p-4">
              <legend className="px-1 font-semibold text-slate-800">Situación laboral</legend>
              <div className="mt-2 flex flex-wrap gap-x-6 gap-y-3">
                <label className="flex items-center gap-2">
                  <input type="radio" name="employmentStatus" value="ocupado" required />
                  <span>Empleado/a</span>
                </label>
                <label className="flex items-center gap-2">
                  <input type="radio" name="employmentStatus" value="desempleado" />
                  <span>Desempleado/a</span>
                </label>
              </div>
            </fieldset>
            <div className="card">
              <p className="font-semibold text-slate-800">¿Tiene el DARDE activo?</p>
              <div className="mt-2 flex gap-6">
                <label className="flex items-center gap-2">
                  <input type="radio" name="dardeActive" value="Sí" required />
                  <span>Sí</span>
                </label>
                <label className="flex items-center gap-2">
                  <input type="radio" name="dardeActive" value="No" />
                  <span>No</span>
                </label>
              </div>
            </div>
            <label className="flex items-start gap-3 text-sm text-slate-700">
              <input type="checkbox" name="privacyAccepted" required className="mt-1" />
              <span>
                He leído y acepto la <a href="/politica-privacidad" target="_blank" rel="noopener noreferrer" className="text-sky-600 underline">Política de Privacidad</a>.
              </span>
            </label>
            <div className="flex items-center justify-end gap-4 pt-2">
              <button type="button" onClick={onClose} className="btn-secondary">Cancelar</button>
              <button type="submit" disabled={loading} className="btn-primary">
                {loading
                  ? "Enviando..."
                  : isUnavailable
                    ? "Avisarme cuando esté disponible"
                    : "Confirmar inscripción"}
              </button>
            </div>
            {error && <p className="mt-2 text-sm text-red-600">Error: {error}</p>}
          </form>
        )}
      </div>
    </div>
  );
}
